import styles from './Avatar.module.css';
import { cn } from '@/lib/utils';

export interface AvatarProps {
  /** Image URL. Falls back to initials when absent. */
  src?: string;
  /** Full name — used for initials fallback and aria-label. */
  name?: string;
  /** Avatar diameter. */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS class. */
  className?: string;
}

/** Extracts up to 2 initials from a name string. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
}

/**
 * Circular avatar with image or initials fallback.
 */
export function Avatar({
  src,
  name,
  size = 'medium',
  className,
}: AvatarProps) {
  if (src) {
    return (
      <div
        className={cn(styles.avatar, styles[size], className)}
        aria-label={name}
      >
        <img
          className={styles.image}
          src={src}
          alt={name ?? ''}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(styles.avatar, styles.initials, styles[size], className)}
      aria-label={name}
    >
      {name ? getInitials(name) : '?'}
    </div>
  );
}
